import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, AlertTriangle, ExternalLink } from "lucide-react";
import { Card, Btn, Badge, Spinner } from "@/components/ui-kit";
import api from "@/lib/api";

const DOCS = [["eula", "End User License Agreement & Terms of Use"], ["privacy", "Privacy Policy"], ["key_terms", "Key terms at a glance"]];

function DocInput({ k, label, value, onText, onKeyTerms, warn }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/legal/extract", fd);
      onText(r.data.text, { placeholders: r.data.placeholders, draft: r.data.draft });
      if (k === "eula" && r.data.key_terms) onKeyTerms(r.data.key_terms);
      toast.success(`${file.name} loaded \u2014 review the text before publishing`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not read file"); }
    setBusy(false);
  };
  return (
    <div data-testid={`legal-pub-${k}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="pq-label">{label}</span>
        <label className="inline-flex items-center gap-1 text-[12px] font-semibold text-teal-700 cursor-pointer">
          {busy ? <Spinner className="w-3.5 h-3.5" /> : <Upload size={13} />} Upload .docx / .pdf / .txt
          <input type="file" accept=".docx,.pdf,.txt,.md" className="hidden" onChange={pick} data-testid={`legal-pub-${k}-file`} />
        </label>
      </div>
      <textarea className="pq-input min-h-[140px] font-mono text-[11.5px]" value={value} onChange={(e) => onText(e.target.value)} data-testid={`legal-pub-${k}-text`} />
      {warn && (warn.draft || warn.placeholders?.length > 0) && (
        <p className="mt-1 flex items-start gap-1 text-[11.5px] text-amber-700" data-testid={`legal-pub-${k}-warn`}>
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          {warn.draft ? "Marked as a draft. " : ""}{warn.placeholders?.length ? `Unfilled placeholders: ${warn.placeholders.join(", ")}` : ""}
        </p>
      )}
    </div>
  );
}

function Versions({ rows }) {
  return (
    <div className="space-y-2" data-testid="legal-versions">
      {rows.map((v) => (
        <div key={v.version} className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-[12.5px]" data-testid={`legal-version-${v.version}`}>
          <b className="text-slate-900">v{v.version}</b>
          {v.placeholder && <Badge tone="amber">Placeholder</Badge>}
          <Badge tone={v.material ? "rose" : "slate"}>{v.material ? "Material" : "Minor"}</Badge>
          <span className="text-slate-500">{v.published_at?.slice(0, 16).replace("T", " ")} · {v.published_by}</span>
          <span className="text-slate-600">· {v.acceptances} accepted</span>
          <span className="text-slate-400 break-all">· {v.hash.slice(0, 12)}…</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminLegal() {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ version: "", material: true, eula: "", privacy: "", key_terms: "" });
  const [warn, setWarn] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/legal/versions").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const fromCurrent = async () => {
    const r = await api.get("/legal/clinician");
    setF((x) => ({ ...x, eula: r.data.eula, privacy: r.data.privacy, key_terms: r.data.key_terms }));
    toast.success("Loaded the current published text");
  };
  const latest = rows?.[rows.length - 1];
  const replacing = latest && f.version === latest.version && latest.acceptances === 0;
  const publish = async () => {
    if (!window.confirm(replacing ? `Replace v${f.version} (no one has accepted it yet)?` : `Publish v${f.version}${f.material ? " as a MATERIAL change \u2014 every clinician must re-accept" : ""}?`)) return;
    setBusy(true);
    try {
      await api.post("/admin/legal/publish", { ...f, replace: replacing });
      toast.success(`Version ${f.version} published`);
      setF({ version: "", material: true, eula: "", privacy: "", key_terms: "" });
      setWarn({});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not publish"); }
    setBusy(false);
  };
  if (!rows) return <div className="py-10 grid place-items-center"><Spinner className="w-6 h-6" /></div>;
  const ready = f.version.trim() && f.eula.trim() && f.privacy.trim() && f.key_terms.trim();
  return (
    <div className="grid gap-4 xl:grid-cols-[1fr_2fr]" data-testid="admin-legal">
      <Card className="p-5">
        <div className="flex items-center gap-2 font-semibold text-[14px] mb-3"><FileText size={16} className="text-teal-600" /> Clinician agreement versions</div>
        <Versions rows={rows} />
        <div className="mt-3 flex gap-3 text-[12px]">
          <a href="/legal/clinician/eula" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-teal-700 underline">EULA <ExternalLink size={11} /></a>
          <a href="/legal/clinician/privacy" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-teal-700 underline">Privacy Policy <ExternalLink size={11} /></a>
        </div>
      </Card>
      <Card className="p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block"><span className="pq-label block mb-1">New version</span>
            <input className="pq-input w-32" value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} placeholder={latest ? `after ${latest.version}` : "1.0"} data-testid="legal-pub-version" /></label>
          <label className="flex items-center gap-2 text-[12.5px] text-slate-700 pb-2">
            <input type="checkbox" checked={f.material} onChange={(e) => setF({ ...f, material: e.target.checked })} className="h-4 w-4 accent-teal-700" data-testid="legal-pub-material" />
            Material change (clinicians must re-accept; new orders blocked until they do)
          </label>
          <Btn size="sm" onClick={fromCurrent} data-testid="legal-pub-load-current">Start from current text</Btn>
        </div>
        {replacing && <p className="text-[12px] text-sky-700" data-testid="legal-pub-replacing">v{f.version} hasn't been accepted by anyone yet — publishing will correct it in place.</p>}
        {DOCS.map(([k, label]) => (
          <DocInput key={k} k={k} label={label} value={f[k]} warn={warn[k]}
            onText={(t, w) => { setF((x) => ({ ...x, [k]: t })); if (w) setWarn((x) => ({ ...x, [k]: w })); }}
            onKeyTerms={(t) => setF((x) => ({ ...x, key_terms: x.key_terms || t }))} />
        ))}
        <div className="flex justify-end">
          <Btn variant="primary" onClick={publish} disabled={!ready || busy} data-testid="legal-pub-submit">{busy ? <Spinner className="w-4 h-4" /> : replacing ? `Replace v${f.version}` : "Publish version"}</Btn>
        </div>
      </Card>
    </div>
  );
}
